import type React from "react";
import { type LinkProps, NavLink } from "react-router";
import type { VariantProps } from "tailwind-variants";

import aiButtonIcon from "@/assets/images/ai-button-icon.svg";
import { ButtonLoader } from "@/design-system/ButtonLoader";
import { Tooltip } from "@/design-system/Tooltip";
import { twResolve, twVariants } from "@/helpers/tw-resolve";

export type ButtonBaseProps = {
  children: React.ReactNode;
  title?: string;
  "data-testid"?: string;
  "data-preview-state"?: "hover" | "pressed";
  "aria-label"?: string;
  underline?: boolean;
  emphasis?: boolean;
  fullWidth?: boolean | "untilSm" | "untilMd";
  isLoading?: boolean;
  isCircular?: boolean;
  shape?: "default" | "circular" | "start" | "end";
  inset?: "default" | "compact";
  disabled?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

export type ButtonLinkProps = {
  type: "link";
  href: string;
  state?: LinkProps["state"];
  target?: LinkProps["target"];
  isExternal?: boolean;
  form?: never;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>;
  onBlur?: React.FocusEventHandler<HTMLAnchorElement>;
};

export type ButtonButtonProps = {
  type?: "button";
  href?: never;
  state?: never;
  target?: never;
  isExternal?: never;
  form?: never;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
};

export type ButtonSubmitProps = {
  type?: "submit";
  href?: never;
  state?: never;
  target?: never;
  isExternal?: never;
  form?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
};

export type ButtonProps = ButtonBaseProps &
  (ButtonLinkProps | ButtonButtonProps | ButtonSubmitProps) & {
    variant?: ButtonVariants["variant"];
    tone?: ButtonVariants["tone"];
    size?: ButtonVariants["size"];
    icon?: React.ReactNode;
  };

export function Button({
  children,
  title,
  variant = "filled",
  type,
  "data-testid": dataTestId,
  underline,
  emphasis,
  href,
  target,
  state,
  icon,
  tone = "primary",
  size = "md",
  fullWidth = false,
  disabled,
  isLoading,
  isExternal,
  isCircular,
  shape = "default",
  inset = "default",
  onClick,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  ...otherProps
}: ButtonProps): React.ReactNode {
  const propsCommon = {
    "data-testid": dataTestId,
    "data-ai-button-root": variant === "ai" ? true : undefined,
    className: twResolve(
      buttonVariants({
        variant,
        tone,
        size,
        type,
        shape: isCircular ? "circular" : shape,
        inset,
        fullWidth: fullWidth === true,
      }),
      fullWidth === true && "w-full",
      fullWidth === "untilSm" && "w-full @sm:w-auto",
      fullWidth === "untilMd" && "w-full @md:w-auto",
      emphasis && (size === "sm" ? "text-caption-emphasis" : "text-body-emphasis"),
      emphasis === false && (size === "sm" ? "text-caption" : "text-body"),
      underline && "underline",
    ),
  };

  // The AI variant paints its gradient in a sibling layer that sits behind every
  // child, so the content below needs no elevation of its own.
  const isAi = variant === "ai";

  const Content = (
    <>
      {isAi && <AiButtonBackground />}
      {isAi && !icon ? (
        <span aria-hidden="true" className="flex size-icon-inline shrink-0 items-center justify-center">
          <img alt="" src={aiButtonIcon} className="size-ai-button-icon" />
        </span>
      ) : (
        icon
      )}
      {typeof children === "string" ? <span className="leading-tight">{children}</span> : children}
    </>
  );

  if (type === "link") {
    // Links have no `disabled` attribute, so the click has to be blocked by hand.
    if (isExternal) {
      return (
        <Tooltip tooltip={title}>
          <a
            aria-disabled={disabled}
            rel="noopener noreferrer"
            onClick={(e) => {
              if (disabled) {
                e.preventDefault();
              }

              onClick?.(e);
            }}
            {...{ target, href, onFocus, onBlur }}
            {...propsCommon}
          >
            {Content}
          </a>
        </Tooltip>
      );
    }

    return (
      <Tooltip tooltip={title}>
        <NavLink
          aria-disabled={disabled}
          to={href}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
            }

            onClick?.(e);
          }}
          {...{ state, target, onFocus, onBlur }}
          {...propsCommon}
        >
          {Content}
        </NavLink>
      </Tooltip>
    );
  }

  return (
    <Tooltip tooltip={title}>
      <button
        type={type || "button"}
        disabled={disabled || isLoading}
        {...{ onClick, onFocus, onBlur, onMouseEnter, onMouseLeave }}
        {...propsCommon}
        {...otherProps}
      >
        {Content}
        <ButtonLoader isVisible={isLoading} />
      </button>
    </Tooltip>
  );
}

// Each variant paints with the `--button-*` colour slots that the tone fills in
// below. `hover` and `pressed` are project variants that already exclude disabled
// buttons and aria-disabled links, so every state needs a single line.
const commonButtonStyles = [
  "button-border appearance-none overflow-hidden whitespace-nowrap rounded-lg border-solid font-sans select-none",
  "transition-colors duration-button motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2",
];

const filledButtonStyles = [
  ...commonButtonStyles,
  "bg-(--button-action) border-(--button-action) text-(--button-filled-text)",
  "hover:bg-(--button-action-hover) hover:border-(--button-action-hover)",
  "pressed:bg-(--button-action-pressed) pressed:border-(--button-action-pressed)",
  "disabled:bg-(--button-filled-disabled-bg) disabled:border-(--button-filled-disabled-bg) disabled:text-(--button-filled-disabled-text)",
];

const softButtonStyles = [
  ...commonButtonStyles,
  "bg-(--button-soft) border-(--button-soft) text-(--button-text)",
  "hover:bg-(--button-soft-hover) hover:border-(--button-soft-hover)",
  "pressed:bg-(--button-soft-pressed) pressed:border-(--button-soft-pressed)",
  "disabled:bg-(--button-soft-disabled-bg) disabled:border-(--button-soft-disabled-bg) disabled:text-(--button-soft-disabled-text)",
];

const outlineButtonStyles = [
  ...commonButtonStyles,
  "border-(--button-action) bg-transparent text-(--button-text)",
  "hover:bg-(--button-outline-hover)",
  "pressed:bg-(--button-outline-pressed)",
  "disabled:border-(--button-outline-disabled-border) disabled:bg-transparent disabled:text-(--button-outline-disabled-text)",
];

const subtleButtonStyles = [
  ...commonButtonStyles,
  "border-transparent bg-transparent text-(--button-text)",
  "hover:bg-(--button-subtle-hover) hover:border-(--button-subtle-hover)",
  "pressed:bg-(--button-subtle-pressed) pressed:border-(--button-subtle-pressed)",
  "disabled:border-transparent disabled:bg-transparent disabled:text-(--button-subtle-disabled-text)",
];

const aiButtonStyles = [
  "relative isolate appearance-none overflow-hidden whitespace-nowrap rounded-lg border-0 bg-transparent font-sans text-text-inverse select-none",
  "disabled:cursor-not-allowed disabled:text-text-on-disabled",
  "focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2",
  "ai-button-gradient-state",
];

function AiButtonBackground(): React.ReactNode {
  return (
    <span
      aria-hidden="true"
      data-ai-button-background
      className="pointer-events-none absolute inset-0 -z-raised ai-button-background"
    />
  );
}

export type ButtonTone = "primary" | "error" | "warning" | "success" | "light";
export type ButtonVisualVariant = "filled" | "soft" | "outline" | "subtle";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

export const buttonVariants = twVariants({
  base: [
    "relative flex w-fit max-w-full shrink-0 cursor-pointer items-center justify-center truncate text-center no-underline",
    "transition-transform button-disabled-slots focus-visible:outline-hidden disabled:cursor-default",
  ],
  variants: {
    // Fills in the colour slots the variants below paint with.
    tone: {
      primary: "button-tone-primary",
      error: "button-tone-error",
      warning: "button-tone-warning",
      success: "button-tone-success",
      // Sits on dark backgrounds, so it restyles the disabled slots too.
      light: "button-tone-light",
    },
    variant: {
      filled: filledButtonStyles,
      soft: softButtonStyles,
      outline: outlineButtonStyles,
      subtle: subtleButtonStyles,
      ghostPrimary:
        "inline-flex text-text-brand focus-visible:ring-border-brand disabled:text-text-disabled! hocus:text-text-brand-dark",
      ghostSecondary: "inline-flex text-text-primary focus-visible:ring-border-brand disabled:text-text-disabled",
      ai: aiButtonStyles,
    },
    // The AI gradient runs corner to corner, so its angle follows the height.
    // Harmless on the other variants, which never read the variable.
    size: {
      sm: "h-control-sm gap-xs px-ai-button-sm ai-button-angle-sm",
      md: "h-control-md gap-sm px-ai-button-md ai-button-angle-md",
      lg: "h-control-lg gap-sm px-ai-button-lg ai-button-angle-lg",
      xl: "h-control-xl gap-sm px-ai-button-xl ai-button-angle-xl",
    },
    shape: {
      default: "rounded-lg",
      circular: "rounded-full",
      start: "rounded-tl-lg rounded-r-none rounded-bl-none",
      end: "rounded-l-none rounded-tr-lg rounded-br-none",
    },
    inset: {
      default: "",
      compact: "px-space-xs",
    },
    fullWidth: {
      true: "w-full",
      false: "w-fit",
    },
    content: {
      label: "button-typography",
      body: "text-body",
      icon: "",
    },
    pressAnimation: {
      true: "button-press",
      false: "",
    },
    // The rendered element. Only keys the ghost-link compound variant below.
    type: {
      button: "",
      submit: "",
      link: "",
      label: "",
    },
  },
  compoundVariants: [
    {
      variant: ["ghostPrimary", "ghostSecondary"],
      className: "h-auto! rounded-none! p-0!",
    },
    {
      variant: ["ghostPrimary", "ghostSecondary"],
      type: "link",
      className: "hover:underline",
    },
  ],
  defaultVariants: {
    tone: "primary",
    variant: "filled",
    size: "md",
    shape: "default",
    inset: "default",
    fullWidth: false,
    content: "label",
    pressAnimation: true,
    type: "button",
  },
});

type ButtonVariants = VariantProps<typeof buttonVariants>;
